import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';

import * as Objects from '@core/objects';
import { FilesListItemMenuClick } from '@core/components/files/lists/files-list-item-menu-click';
import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { VersionsViewService } from '@app/services/app/versions.service';
import { UrlService } from '@core/services/url/url.service';
import { FileDownloadService } from '@core/services/download/file-download.service';
import { AppService } from '@app/services/app/app.service';

import { VersionFeedback } from '@core/models/version-feedback';
import { VersionShare, VersionShareEntryDetail, VersionShareOptionName, getVersionShareOption, isVersionShareOpen, isFeedbackVersionShare } from '@core/models/version-share.model';
import * as flContexts from '@core/constants/files-list-contexts.constants';
import { InterfaceListenerService } from '@core/services/interface-listener/interface-listener.service';

@Component({
    selector: 'version-details-files-notes',
    templateUrl: './files-notes.component.html',
    styleUrls: ['./files-notes.component.scss']
})
export class VersionsDetailsContentFilesNotesComponent implements OnInit, OnDestroy, OnChanges {

    protected readonly PLUGIN_FEEDBACK = 'share_feedback';
    protected readonly VERSION_SHARE_FEEDBACK_CONTEXT = 'version-share-feedback';
    protected readonly SIGNATURE_OPTION_TYPE_CFR11 = 'cfr11';

    public readonly LOADING = 0;
    public readonly DONE = 1;
    public readonly ERROR = 2;

    public readonly PREVIEW_LOADING = 0;
    public readonly PREVIEW_DONE = 1;

    @Input() menu: Array<ThreeDotsMenuItem> = [];
    @Input() version: Objects.Version = null;
    //  files shared in this share with this user: to check if this file was shared with me. Could be also from other versions all shared together
    @Input() sharedWithMe: Array<number>; 
    // @deprecated oct-2026: legacy share feedback url call response, replaced by myShare
    // @Input() share: any; // the user share
    // @deprecated oct-2026: legacy share feedback bubbles url call response, replaced by versionShares
    // @Input() feedbacks: Array<VersionFeedback> = null;
    // oct-2026: current user's open approval/feedback entry on this version (null when not a recipient)
    @Input() myShare: VersionShareEntryDetail = null;
    // oct-2026: every share entry on this version (all recipients)
    @Input() versionShares: Array<VersionShare> = null;
    @Output() action = new EventEmitter<FilesListItemMenuClick>();
    @Output() actionClick = new EventEmitter<Objects.AmsFile>();

    public files: Array<Objects.AmsFile> = null;
    public sharedFileIds: Array<number> = null;
    public notesCounter: Array<Objects.AnnotationsCounter> = [];
    public selectedFile: Objects.AmsFile = null;
    public status:number = this.LOADING;
    public previewStatus:number = this.PREVIEW_LOADING;
    public notesUrlPrefix: string = '';
    public annotationsUrlPrefix: string = '';
    public annotationsSaveUrlPrefix: string = '';
    public filesContext: string = flContexts.CTX.APP_ARTWORK_DETAILS;
    public signatureOptionType: string;
    public previewContext: string = flContexts.CTX.DEFAULT;
    public canCreateNotes: boolean = false;

    protected filesSubscriptor: Subscription;
    protected filesNotesSubscriptor: Subscription;
    protected appSubscriptor: Subscription;
    protected iListenerSubs: Subscription;
    protected user: Objects.User = null;
    // @deprecated oct-2026: the open status now comes from myShare
    // protected userFeedback: VersionFeedback = null;

    private _accessFeedbacks: boolean | null = null;

    /**
     * Track previous preview state to prevent unnecessary viewer reinitialization
     * Only reload viewer when canCreateNotes or save URL actually changes
     */
    private previousCanCreateNotes: boolean = false;
    private previousAnnotationsSaveUrlPrefix: string = '';
    private previousPreviewContext: string = '';

    constructor(
        private urlSrv: UrlService,
        private cdr: ChangeDetectorRef,
        private viewVersionsSrv: VersionsViewService,
        private appSrv: AppService,
        private fileDownloadSrv: FileDownloadService,
        private iListener: InterfaceListenerService ) { }

    // public getters 
    get accessFeedbacks(): boolean {
        return this._accessFeedbacks || false; // Return false if _accessFeedbacks is null
    }

    ngOnInit() {
        this.filesSubscriptor = this.viewVersionsSrv.selectAssets().subscribe((assets: Objects.AmsFile[] ) => {
            if(assets != null){
                this.files = assets;
                this.loadSharedFilesNotesCounter();
                if (this.selectedFile != null) {
                    const file = this.files.find((f: Objects.AmsFile) => f.uuid === this.selectedFile.uuid);
                    if (file) {
                        this.selectedFile = file;
                    }
                }
            }
        });

        this.filesNotesSubscriptor = this.viewVersionsSrv.selectFeedbackFileNotesCounter().subscribe((counter: Objects.AnnotationsCounter[] ) => {
            // TODO may-2023: versions files notes should be counter
            this.notesCounter = counter;
            // console.log(" FILEs Notes counter get ", counter)
            this.init();
        });

        // TODO: get version id form subscriptor and set url prefixes...
        this.appSubscriptor = this.appSrv.getSession().subscribe( (session: any) => {
            this.user = session.current.user;
            this.signatureOptionType = session?.options?.organization.version_approvals_signature_type;
            this.updateFilesContext();

            this._accessFeedbacks = session?.options?.plugins?.some((pa) => pa == this.PLUGIN_FEEDBACK);
        //     if(this.accessFeedbacks){
        //         this.viewVersionSrv.loadShareFeedback(this.user);
        //         this.initFeedbacks();
        //     }else
        //         this.initView();
        });

        this.iListenerSubs = this.iListener.getVersionFeedbackDone().subscribe((version: Objects.Version) => {
            // Handle the version feedback 'onDone' event here to reload the notes counter.
            // hide the selected file preview if the feedback is done
            if(this.version != null && version!=null && version.id == this.version.id){
                // put the file preview in pdf viewer to loading as we wait for Ws to arrive with new feedback status
                if (this.selectedFile) {
                    this.updateSelectedFilePreviewIfNeeded();
                }
                this.loadSharedFilesNotesCounter();
            }
        });

        // @deprecated oct-2026: the open status now comes from myShare
        // if(this.feedbacks)
        //     this.loadMyFeedback();
    }

    ngOnDestroy() {
        this.filesSubscriptor.unsubscribe();
        this.filesNotesSubscriptor.unsubscribe();
        this.appSubscriptor.unsubscribe();
        this.iListenerSubs.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if(changes.versionShares){
            this.updateFilesContext();
        }
        if((changes.myShare && !changes.myShare.firstChange) || changes.versionShares){
            if (this.selectedFile) {
                // Only reload preview if meaningful state changed
                this.updateSelectedFilePreviewIfNeeded();
            }
        }
    }
    

    init() : void {
        if(this.myShare != null){
            this.annotationsSaveUrlPrefix = "/web/share/feedback/"+this.myShare.entry.token+"/versions/"+this.version.id;
        }
        if(this.files != null && this.notesCounter != null){
            // set urls for notes management.
            this.notesUrlPrefix = "/p/feedback/versions/"+this.version.id;
            this.annotationsUrlPrefix = "/versions/"+this.version.id+"/feedbacks";
            
            this.status = this.DONE;
        }
    }

    onMenuClick(menu: FilesListItemMenuClick) {
        this.action.emit(menu);
        
    }

    // This now opens the preview of the file inline as messages
    actionFileClick(file: Objects.AmsFile){
        if(this.selectedFile != null && this.selectedFile.id == file.id){
            this.selectedFile = null;
        } else {
            // Direct call with recalculated state (user clicked, state is definitely fresh)
            this.selectedFile = file;
            const canCreateNotes = this.calculateCanCreateNotes();
            const saveUrlPrefix = this.calculateAnnotationsSaveUrlPrefix();
            const previewCtx = this.calculatePreviewContext(canCreateNotes);
            this.setAnnotationsControl(file, canCreateNotes, previewCtx, saveUrlPrefix);
            
            // Update tracked state
            this.previousCanCreateNotes = canCreateNotes;
            this.previousAnnotationsSaveUrlPrefix = saveUrlPrefix;
            this.previousPreviewContext = previewCtx;
        }
    }

    onActionDownloadClick(file: Objects.AmsFile) {
        this.fileDownloadSrv.downloadVersionAsset(file);
    }

    /**
     * Set up annotations control for the selected file
     * @param file The file to preview
     * @param canCreateNotes Whether user can create notes
     * @param previewCtx The preview context (feedback or default)
     * @param saveUrlPrefix The URL prefix for saving annotations
     */
    setAnnotationsControl(file: Objects.AmsFile, canCreateNotes?: boolean, previewCtx?: string, saveUrlPrefix?: string): void {

        this.previewStatus = this.PREVIEW_LOADING;
        this.cdr.detectChanges(); // to force the view to update when the file previewStatus is updated
        
        // If parameters not provided, calculate them (fallback for direct calls)
        if (canCreateNotes === undefined) {
            canCreateNotes = this.calculateCanCreateNotes();
        }
        if (previewCtx === undefined) {
            previewCtx = this.calculatePreviewContext(canCreateNotes);
        }
        if (saveUrlPrefix === undefined) {
            saveUrlPrefix = this.calculateAnnotationsSaveUrlPrefix();
        }
        
        this.canCreateNotes = canCreateNotes;
        this.previewContext = previewCtx;
        
        if (canCreateNotes) {
            this.annotationsSaveUrlPrefix = saveUrlPrefix;
        } else {
            this.annotationsSaveUrlPrefix = "";
        }
        this.previewStatus = this.PREVIEW_DONE;
    }

    protected loadSharedFilesNotesCounter(): void {
        if(this.files != null && this.files.length > 0 ){
            // NO need to filter files to load notes only for the shared ones
            this.viewVersionsSrv.loadFeedbackFilesNotesCounter();
            this.init();
        } 
    }

    // @deprecated oct-2026: legacy share feedback bubbles url call, the open status now comes from myShare
    // protected loadMyFeedback(){
    //     if (!this.accessFeedbacks || this.user == null){
    //         return;
    //     }

    //     if (this.version != null && this.feedbacks !=null){
    //         this.userFeedback = null;
    //         let myFBs = this.feedbacks.filter((f: VersionFeedback) => f.user.id == this.user.id );
    //         this.userFeedback = myFBs.length > 0 ? myFBs[0] : null;
    //     }
    // }

    onSavedAnnotations(e:any) {
        this.loadSharedFilesNotesCounter();
    }

    protected updateFilesContext() : void {
        if( this.versionShares?.some((vs: VersionShare) => isFeedbackVersionShare(vs)) && this.signatureOptionType === this.SIGNATURE_OPTION_TYPE_CFR11){
            this.filesContext = flContexts.CTX.APP_ARTWORK_DETAILS_FEEDBACK;
        } else {
            this.filesContext = flContexts.CTX.APP_ARTWORK_DETAILS;
        }
    }

    /**
     * Smart preview update: only reload viewer if meaningful state changed
     * Prevents unnecessary viewer destruction/recreation cycles
     * Only reloads when canCreateNotes or save URL actually changes
     */
    private updateSelectedFilePreviewIfNeeded(): void {
        const newCanCreateNotes = this.calculateCanCreateNotes();
        const newAnnotationsSaveUrlPrefix = this.calculateAnnotationsSaveUrlPrefix();
        const newPreviewContext = this.calculatePreviewContext(newCanCreateNotes);

        // Only reload if state actually changed
        if (this.previousCanCreateNotes !== newCanCreateNotes ||
            this.previousAnnotationsSaveUrlPrefix !== newAnnotationsSaveUrlPrefix) {
            
            this.setAnnotationsControl(this.selectedFile, newCanCreateNotes, newPreviewContext, newAnnotationsSaveUrlPrefix);
            
            // Store new state
            this.previousCanCreateNotes = newCanCreateNotes;
            this.previousAnnotationsSaveUrlPrefix = newAnnotationsSaveUrlPrefix;
            this.previousPreviewContext = newPreviewContext;
        } 
       
    }

    /**
     * Calculate if user can create notes based on current conditions
     */
    private calculateCanCreateNotes(): boolean {
        return (getVersionShareOption(this.myShare, VersionShareOptionName.GET_COMMENTS) && 
                this.sharedWithMe?.includes(this.selectedFile.id) && 
                this.myShare.entry.user.id == this.user?.id &&
                isVersionShareOpen(this.myShare)
        );
    }

    /**
     * Calculate the annotations save URL based on canCreateNotes state
     */
    private calculateAnnotationsSaveUrlPrefix(): string {
        if (this.calculateCanCreateNotes()) {
            return "/web/share/feedback/"+this.myShare.entry.token+"/versions/"+this.version.id;
        }
        return "";
    }

    /**
     * Calculate the preview context based on canCreateNotes state
     */
    private calculatePreviewContext(canCreateNotes: boolean): string {
        return canCreateNotes ? this.VERSION_SHARE_FEEDBACK_CONTEXT : flContexts.CTX.DEFAULT;
    }

    onRefreshSignaturesNeeded() {
        this.viewVersionsSrv.refreshAssetsSignatures();
    }

    /**
     * TrackBy function for *ngFor loops over files
     * Prevents unnecessary re-creation of child components when files array updates
     * but individual files haven't changed (e.g., during signature refresh)
     * 
     * Critical for: pdf-annotations-viewer component (expensive to recreate)
     * 
     * @param index Array index
     * @param file File object
     * @returns Unique identifier for the file (its ID)
     */
    public trackByFileId(index: number, file: Objects.AmsFile): string {
        return file.uuid;
    }



}
